import { useState } from "react"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { startSubscription, type PlanChoice } from "@/features/billing/services/razorpayCheckout"
import { track } from "@/lib/analytics"
import { metricExhausted, openPaywall, type QuotaMetric } from "@/lib/quotaGate"
import "./pro-button.css"

const OFFICER: QuotaMetric = "nira.officer_reading"

/** Where an upgrade was asked for, for the funnel (PostHog `upgrade_clicked.source`). */
export type UpgradeSource =
  | "officer_card" | "appeal_row" | "grounds" | "pricing" | "research_card" | "rail_lock" | "preview_door"
  | "sim_upload" | "notice_limit" | "appeal_limit" | "rerun" | "reply_draft"

/**
 * Which press of the journey an `upgrade_clicked` is: `door` opens the gate, `overlay` is Start Pro inside it,
 * `direct` is a Pro button pressed on its own. Each user click fires exactly one event.
 */
export type UpgradeStep = "door" | "overlay" | "direct"

/**
 * The one way to a paid plan (Pro by default, or `planChoice`). With billing on, it opens Razorpay checkout straight away; without it (or
 * when checkout cannot start) the Pro plan dialog shows the plan and price. `ranOut` when the reader
 * has used what they had, so the dialog says so; otherwise it simply offers Pro.
 */
export function useUpgrade(source: UpgradeSource, metric: QuotaMetric = OFFICER, ranOut = false, step: UpgradeStep = "direct", planChoice: PlanChoice = "pro") {
  const me = useMeStore((s) => s.me)
  const [busy, setBusy] = useState(false)
  const plan = () => openPaywall(me?.entitlements ?? null, metric, { upgrade: !ranOut, force: true })
  const go = async () => {
    track("upgrade_clicked", { source, metric, plan: planChoice, step })
    if (!me?.billing_enabled || me.entitlements?.subscribed) return plan()
    setBusy(true)
    try {
      const result = await startSubscription(planChoice, { name: me.display_name ?? undefined, email: me.email })
      track("checkout_result", { source, result })
      if (result !== "active" && result !== "dismissed") plan()
    } catch {
      track("checkout_result", { source, result: "error" })
      plan()
    } finally {
      setBusy(false)
    }
  }
  return { go, busy }
}

export function ProButton({ metric = OFFICER, label = "Start Pro", ranOut = false, source }: {
  metric?: QuotaMetric
  label?: string
  ranOut?: boolean
  source: UpgradeSource
}) {
  const { go, busy } = useUpgrade(source, metric, ranOut)
  return (
    <button type="button" className="ncf-btn primary npro-btn" disabled={busy} onClick={() => void go()}>
      {busy ? "Opening checkout…" : label}
    </button>
  )
}

/** Where the reader stands on a metric: free one left, Pro with some left, used up, or not counted. */
export function useAllowance(metric: QuotaMetric): "free" | "pro" | "used" | "open" {
  const e = useMeStore((s) => s.me?.entitlements ?? null)
  // No entitlements reported (billing off, as locally): nothing to count against.
  if (!e || e.exempt) return "open"
  if (metricExhausted(e, metric)) return "used"
  return e.subscribed ? "pro" : "free"
}

export const useOfficerAllowance = () => useAllowance(OFFICER)

/**
 * The officer's reading's single call to action. While a reading is left, it runs one (the first
 * is free); once the free one is used, the same place is the way to Pro.
 */
export function OfficerCta({ onRead, busy = false }: { onRead: () => void; busy?: boolean }) {
  const state = useOfficerAllowance()
  const e = useMeStore((s) => s.me?.entitlements ?? null)
  const m = e?.metrics.find((x) => x.metric === OFFICER)
  if (state === "used" && (e?.plan_state === "pro" || e?.plan_state === "grace") && m && m.window === "day" && m.limit !== null) {
    // Pro with the day's readings used: nothing to sell, only when it comes back.
    return (
      <div className="npro-cta">
        <span className="npro-note">{m.used} of {m.limit} used today. Resets at midnight.</span>
      </div>
    )
  }
  if (state === "used") {
    return (
      <div className="npro-cta">
        <ProButton ranOut source="officer_card" />
        <span className="npro-note">You have used your free officer's reading. Pro reads every notice this way.</span>
      </div>
    )
  }
  return (
    <div className="npro-cta">
      <button type="button" className="ncf-btn primary" disabled={busy} onClick={onRead}>
        {busy ? "Starting the officer's reading…" : "Read it the officer's way"}
      </button>
      {state === "free" ? <span className="npro-note">Your first one is free. After that, it is part of Pro.</span> : null}
    </div>
  )
}
